import { Component, ViewChild } from "@angular/core";
import { FormGroup, FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import moment from "moment";
import { FilterOption } from "src/app/ui/models/domains/filters/filter-option";
import { FilterOptionType } from "src/app/ui/models/domains/filters/filter-option-type.enum";
import { MaskInput } from "src/app/ui/models/domains/masks/mask.model";
import { PagingRequest } from "src/app/ui/models/domains/paginator/paging-request";
import { CompanyFinancialReportsPageableResult } from "src/app/ui/models/domains/pbm/companies/company-financial-reports-pageable-result.model";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { LoadingService } from "src/app/ui/services/loading.service";
import { CompanyService } from "src/app/ui/services/pbm/company.service";
import { Toast } from "../../../toast/toast";
import { FilterFormValidator } from "../../sales-histories/validators/filter-form-validator";
import { AppContext } from "../../../../contexts/app-context";
import { PageableResult } from "../../../../models/domains/paginator/pageable-result";
import { UserService } from "../../../../services/user.service";
import { UserProfile } from "../../../../models/domains/pbm/user/user-profile";
import { CompanyFinancialReports } from "../../../../models/domains/pbm/companies/company-financial-reports.model";
import { RemoteGatewayFactory } from "../../../../gateways/remote-gateway-factory";
import { UserPermissions } from "src/app/ui/models/domains/pbm/user/user-permissions";
import { SelectionModel } from "@angular/cdk/collections";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";

@Component({
  selector: "company-financial-report",
  templateUrl: "./company-financial-report.component.html",
  styleUrls: ["./company-financial-report.component.scss"],
})
export class CompanyFinancialReportComponent {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  displayedColumns: string[] = ["select", "fiscalNumber", "name", "beneficiaries", "dates", "download"];
  dataSource = new MatTableDataSource<CompanyFinancialReports>();
  selection = new SelectionModel<CompanyFinancialReports>(true, []);
  companyFinancialReportsPageableResult: CompanyFinancialReportsPageableResult;
  dataMask = MaskInput.getDateMask();
  pageableResult: PageableResult = new PageableResult();
  userProfile: UserProfile;
  isSelectedList: boolean = false;
  totalItens: number = 0;
  filterForm: FormGroup = new FormGroup({
    startDate: new FormControl(moment().subtract(1, "M").toDate()),
    endDate: new FormControl(moment().toDate()),
    productManufacturer: new FormControl([]),
    productType: new FormControl([]),
    searchType: new FormControl(1),
    filterText: new FormControl(""),
  });
  selectedList: CompanyFinancialReports[] = [];
  public count: number = 0;
  public networksFoundList = new MatTableDataSource<CompanyFinancialReports>([]);
  public fiscalNumberMask = MaskInput.getFicalNumberMask();
  private pageIndex: number;

  constructor(
    private router: Router,
    private companyService: CompanyService,
    private loadingService: LoadingService,
    private toast: Toast,
    private remoteGatewayFactory: RemoteGatewayFactory,
    private context: AppContext,
    private userService: UserService,
  ) { }

  hasAccessToViewCompanyFinancialReport(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.ViewCompanyFinancialReport);
  }

  private buildFilters(): FilterOption[] {
    const startDate = this.filterForm.get("startDate").value;
    const endDate = this.filterForm.get("endDate").value;
    const fiscalNumber = this.filterForm.get("filterText").value;
    const allcompanies = true;

    const startDateFilter = new FilterOption(FilterOptionType.Text, "startDateFilter", "startDateFilter");
    startDateFilter.setValue(moment(startDate, "DD/MM/YYYY").format("DDMMYYYY"));

    const endDateFilter = new FilterOption(FilterOptionType.Text, "endDateFilter", "endDateFilter");
    endDateFilter.setValue(moment(endDate, "DD/MM/YYYY").format("DDMMYYYY"));

    const fiscalNumberFilter = new FilterOption(FilterOptionType.Text, "fiscalNumberFilter", "fiscalNumberFilter");
    fiscalNumberFilter.setValue(fiscalNumber.replace(/[./-]/g, ""));

    const allCompaniesFilter = new FilterOption(FilterOptionType.CheckBox, "allCompaniesFilter", "allCompaniesFilter");
    allCompaniesFilter.setValue(allcompanies);

    return [startDateFilter, endDateFilter, allCompaniesFilter, fiscalNumberFilter];
  }

  private buildPagingRequest(event: { pageIndex: number; pageSize: number }): PagingRequest {
    const currentPage = event.pageIndex ? event.pageIndex + 1 : 1;
    const pagingRequest = new PagingRequest();
    pagingRequest.setStart(currentPage);
    pagingRequest.setEnd(this.paginator.pageSize);
    pagingRequest.setFilters(this.buildFilters());
    return pagingRequest;
  }

  async searchWithFilters(event: { pageIndex?: number; pageSize?: number } = {}): Promise<void> {
    if (!this.hasAccessToViewCompanyFinancialReport()) return;
    const ref = this.loadingService.beginLoading();
    const filterFormValidator = new FilterFormValidator();

    if (filterFormValidator.validate(this.filterForm).hasErrorMessages()) {
      this.toast.showWarningToast(filterFormValidator.getValidationResult().getErrorMessage());
      this.loadingService.finishLoading(ref);
      return;
    }

    if (!event.pageIndex) event.pageIndex = 0;
    if (!event.pageSize) event.pageSize = 10;
    this.pageIndex = event.pageIndex;

    try {
      const pagingRequest = this.buildPagingRequest(event as { pageIndex: number; pageSize: number });
      this.companyFinancialReportsPageableResult = await this.companyService.getCompanyFinancialReports(pagingRequest);
      this.dataSource.data = this.companyFinancialReportsPageableResult.getData();
      this.totalItens = this.companyFinancialReportsPageableResult.getTotal();
      this.dataSource._updatePaginator(this.totalItens);
      this.selection.clear();

      if (this.companyFinancialReportsPageableResult.getData().length === 0) {
        this.toast.showWarningToast("Nenhum relatório encontrado.");
      }
    } catch (e) {
      this.handleError(e, "Erro ao buscar relatórios.");
    } finally {
      this.loadingService.finishLoading(ref);
    }
  }

  public getSalesLength(): number {
    return this.companyFinancialReportsPageableResult ? this.companyFinancialReportsPageableResult.getTotal() : 0;
  }

  public getPageIndex(): number {
    return this.pageIndex;
  }

  hasCompanyFinancialReport(): boolean {
    return !!this.companyFinancialReportsPageableResult;
  }

  hasCompanyFinancialReportItem(): boolean {
    return !!this.companyFinancialReportsPageableResult && this.companyFinancialReportsPageableResult.getData().length > 0;
  }

  hasSocialNumber(socialNumber: string): boolean {
    return !!socialNumber;
  }

  formatDate(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let input = inputElement.value.replace(/\D/g, "");
    if (input.length <= 2) {
      inputElement.value = input;
    } else if (input.length <= 4) {
      inputElement.value = `${input.slice(0, 2)}/${input.slice(2)}`;
    } else {
      inputElement.value = `${input.slice(0, 2)}/${input.slice(2, 4)}/${input.slice(4, 8)}`;
    }
  }

  formatFiscalNumber(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let input = inputElement.value.replace(/\D/g, "");
    if (input.length <= 2) {
      inputElement.value = input;
    } else if (input.length <= 5) {
      inputElement.value = `${input.slice(0, 2)}.${input.slice(2)}`;
    } else if (input.length <= 8) {
      inputElement.value = `${input.slice(0, 2)}.${input.slice(2, 5)}.${input.slice(5)}`;
    } else if (input.length <= 12) {
      inputElement.value = `${input.slice(0, 2)}.${input.slice(2, 5)}.${input.slice(5, 8)}/${input.slice(8)}`;
    } else {
      inputElement.value = `${input.slice(0, 2)}.${input.slice(2, 5)}.${input.slice(5, 8)}/${input.slice(8, 12)}-${input.slice(12, 14)}`;
    }
  }

  public storageSearchPreferences(fiscalNumber: string): void {
    this.userService.setUserPreferences(fiscalNumber ? fiscalNumber.replace(/[./-]/g, "") : "");
    this.userProfile = this.userService.getUserProfile();
  }

  backToHome(): void {
    this.router.navigate([RoutePath.Home]);
  }

  resetFilters(): void {
    this.filterForm.reset({
      startDate: moment().subtract(1, "M").toDate(),
      endDate: moment().toDate(),
      productManufacturer: [],
      productType: [],
      searchType: 1,
      filterText: "",
    });
  }

  async downloadReport(row: CompanyFinancialReports): Promise<void> {
    await this.handleDownload(
      [row.getCompanyFinancialReportKey()],
      "RelFechamentoFinanc - " +
      row.getFiscalNumber() +
      " - " +
      row.getCompanyName() +
      " - " +
      moment(row.getCycleBalanceEndDate()).format("YYYY-MM-DD") +
      " - " +
      moment().format("YYYY-MM-DD") +
      ".xlsx"
    );
  }

  async downloadSelectedReports(): Promise<void> {
    await this.handleDownload(
      this.selection.selected.map((report) => report.getCompanyFinancialReportKey()),
      "FechamentoFinanceiro.zip"
    );
  }

  private async handleDownload(keys: string[], filename: string): Promise<void> {
    const loadingRef = this.loadingService.beginLoading();
    const link = document.createElement("a");
    const gateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
    try {
      const fileData = await gateway.downloadAsPost(
        this.context.getBackendUrl() + "/api/company/financial-report-files",
        keys,
        [],
      );
      link.href = window.URL.createObjectURL(fileData);
      link.target = "_blank";
      link.download = filename;
      link.click();
      this.toast.showSuccessToast("Relatórios baixados com sucesso.");
    } catch (e) {
      this.toast.showWarningToast("Erro ao baixar os relatórios.");
    } finally {
      this.loadingService.finishLoading(loadingRef);
    }
  }

  onPressEnterSearchCompanies(event: KeyboardEvent): void {
    if (event.key === "Enter") this.searchWithFilters();
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach((row) => this.selection.select(row));
  }

  checkboxLabel(row?: CompanyFinancialReports): string {
    if (!row) {
      return `${this.isAllSelected() ? "deselect" : "select"} all`;
    }
    return `${this.selection.isSelected(row) ? "deselect" : "select"} row`;
  }

  private handleError(error: any, defaultMessage: string): void {
    this.toast.showWarningToast(error?.error?.Data?.ErrorMessage || defaultMessage);
  }
}
